/**
 * Rehabilitación y estética dental, y armonización orofacial: casos con su planificación y sesiones firmadas.
 * Las dos especialidades comparten la mecánica (caso → sesiones → finalizar) y cambian en sus listas y en lo que se registra.
 */
import { piezaValida } from "./odontograma.ts";

export const TIPOS_ESTETICA = ["rehabilitacion", "armonizacion", "atm", "periodoncia"] as const;
export type TipoEstetica = (typeof TIPOS_ESTETICA)[number];

export const TITULO_ESTETICA: Record<TipoEstetica, string> = { rehabilitacion: "Rehabilitación y estética dental", armonizacion: "Armonización orofacial", atm: "ATM y bruxismo", periodoncia: "Periodoncia" };

/** ATM y bruxismo: placas oclusales, desgaste selectivo y toxina botulínica para el bruxismo. */
export const PROCEDIMIENTOS_ATM = [
  "Placa oclusal orgánica (miorrelajante)", "Entrega e instalación de la placa", "Ajuste y control de la placa", "Desgaste selectivo", "Toxina botulínica para bruxismo", "Control de ATM y bruxismo",
] as const;
export const PROC_PLACA_ENTREGA = "Entrega e instalación de la placa";
export const PROC_DESGASTE = "Desgaste selectivo";
export const PROC_TOXINA_ATM = "Toxina botulínica para bruxismo";
export const ZONAS_ATM = ["Masetero derecho", "Masetero izquierdo", "Temporal derecho", "Temporal izquierdo", "Pterigoideo lateral derecho", "Pterigoideo lateral izquierdo"] as const;
export const ARCADAS_ATM = ["Superior", "Inferior"] as const;
export const MATERIALES_PLACA = ["Acrílico termocurable", "Acrílico autopolimerizable", "Resina impresa en 3D", "Acetato termoformado"] as const;

/** Periodoncia: fases del tratamiento periodontal (control de la causa, reevaluación, cirugía y mantenimiento). */
export const PROCEDIMIENTOS_PERIO = [
  "Evaluación periodontal (sondaje y registro)", "Instrucción de higiene oral", "Profilaxis dental profunda", "Raspado y alisado radicular", "Reevaluación periodontal", "Terapia antimicrobiana",
  "Cirugía periodontal a colgajo", "Gingivectomía y gingivoplastia", "Alargamiento de corona clínica", "Injerto gingival libre", "Injerto de tejido conectivo", "Regeneración tisular o ósea guiada",
  "Férula periodontal", "Mantenimiento periodontal",
] as const;
/** Los procedimientos quirúrgicos piden el consentimiento informado firmado. */
export const PROC_PERIO_QUIRURGICOS: readonly string[] = [
  "Cirugía periodontal a colgajo", "Gingivectomía y gingivoplastia", "Alargamiento de corona clínica", "Injerto gingival libre", "Injerto de tejido conectivo", "Regeneración tisular o ósea guiada",
];
export const ETAPAS_PERIO = ["Fase 1 · Control de la causa (higiene y raspado)", "Fase 2 · Reevaluación", "Fase 3 · Quirúrgica", "Fase 4 · Mantenimiento"] as const;
export const CUADRANTES_PERIO = ["Cuadrante 1 (superior derecho)", "Cuadrante 2 (superior izquierdo)", "Cuadrante 3 (inferior izquierdo)", "Cuadrante 4 (inferior derecho)"] as const;

export const PROCEDIMIENTOS_REHAB = [
  "Diseño digital de sonrisa (DSD)", "Mock-up / encerado diagnóstico", "Carillas de porcelana", "Carillas de resina", "Coronas estéticas", "Incrustaciones estéticas",
  "Resinas estéticas / recontorneado", "Blanqueamiento en consultorio", "Blanqueamiento domiciliario", "Microabrasión del esmalte", "Gingivoplastia estética", "Rehabilitación oral completa",
] as const;
export const ETAPAS_REHAB = ["Diagnóstico y fotografías", "Mock-up o encerado", "Preparación dental", "Provisionales", "Prueba", "Cementación o adhesión", "Ajuste de oclusión", "Control"] as const;
export const MATERIALES_REHAB = ["Porcelana feldespática", "Disilicato de litio (e.max)", "Zirconio", "Resina compuesta", "Cerámica híbrida", "Metal-porcelana", "Peróxido de carbamida", "Peróxido de hidrógeno"] as const;

export const PROCEDIMIENTOS_ARMONIZACION = [
  "Toxina botulínica", "Relleno de ácido hialurónico", "Bioestimulador de colágeno", "Hilos tensores", "Lipolítico de papada", "Perfilado labial", "Rinomodelación",
  "Bichectomía", "Plasma rico en plaquetas (PRP)", "Microneedling", "Peeling",
] as const;
export const ZONAS_ARMONIZACION = [
  "Frente", "Entrecejo", "Patas de gallo", "Ojeras / surco lagrimal", "Pómulos", "Surcos nasogenianos", "Nariz", "Labios", "Comisuras", "Mentón", "Línea mandibular",
  "Masetero", "Papada", "Cuello", "Sonrisa gingival",
] as const;
export const TECNICAS_ARMONIZACION = ["Aguja", "Cánula", "Aguja y cánula", "Retroinyección lineal", "Bolo", "Abanico", "Microgotas"] as const;
export const UNIDADES_ARMONIZACION = ["U", "mL"] as const;
export const ANESTESIA_ARMONIZACION = ["Ninguna", "Tópica", "Infiltrativa", "Bloqueo troncular"] as const;
export const PROXIMA_SEMANAS = [1, 2, 4, 8, 12, 24, 36] as const;

/** Zonas (músculos) que se pueden tratar según la especialidad. */
export const zonasDe = (t: TipoEstetica): readonly string[] => (t === "atm" ? ZONAS_ATM : t === "armonizacion" ? ZONAS_ARMONIZACION : t === "periodoncia" ? CUADRANTES_PERIO : []);

export interface PlanEstetica {
  procedimientos: string[]; piezas: number[]; zonas: string[]; objetivos: string;
  colorInicial: string; colorObjetivo: string; material: string; notas: string;
}
export interface ZonaTratada { zona: string; cantidad: number | null; unidad: string }
export interface SesionEstetica {
  fecha: string; etapa: string; procedimientos: string[]; piezas: number[]; zonas: ZonaTratada[];
  producto: string; marca: string; lote: string; tecnica: string; anestesia: string; color: string; nota: string;
  proximoSemanas: number | null; consentimiento: boolean; contraindicaciones: boolean;
  /** ATM: arcada de la placa entregada o ajustada (Superior o Inferior) */
  arcada?: string;
  /** Periodoncia: cuadrantes tratados y medidas de la sesión (sondaje máximo en mm, sangrado al sondaje y placa en %) */
  cuadrantes?: string[]; sondajeMaxMm?: number | null; sangradoPct?: number | null; placaPct?: number | null;
}

export const planEsteticaVacio = (): PlanEstetica => ({ procedimientos: [], piezas: [], zonas: [], objetivos: "", colorInicial: "", colorObjetivo: "", material: "", notas: "" });
export const sesionEsteticaVacia = (fecha: string): SesionEstetica => ({
  fecha, etapa: "", procedimientos: [], piezas: [], zonas: [], producto: "", marca: "", lote: "", tecnica: "", anestesia: "", color: "", nota: "",
  proximoSemanas: null, consentimiento: false, contraindicaciones: false, arcada: "", cuadrantes: [], sondajeMaxMm: null, sangradoPct: null, placaPct: null,
});

export const catalogosEstetica = {
  PROCEDIMIENTOS_REHAB, ETAPAS_REHAB, MATERIALES_REHAB, PROCEDIMIENTOS_ARMONIZACION, ZONAS_ARMONIZACION, TECNICAS_ARMONIZACION, UNIDADES_ARMONIZACION, ANESTESIA_ARMONIZACION, PROXIMA_SEMANAS,
  PROCEDIMIENTOS_ATM, ZONAS_ATM, ARCADAS_ATM, MATERIALES_PLACA, PROCEDIMIENTOS_PERIO, ETAPAS_PERIO, CUADRANTES_PERIO,
};

export const listaProcedimientos = (t: TipoEstetica): readonly string[] => (t === "rehabilitacion" ? PROCEDIMIENTOS_REHAB : t === "atm" ? PROCEDIMIENTOS_ATM : t === "periodoncia" ? PROCEDIMIENTOS_PERIO : PROCEDIMIENTOS_ARMONIZACION);

export function problemasPlanEstetica(t: TipoEstetica, p: PlanEstetica): string[] {
  const e: string[] = [];
  if (!p.procedimientos.length) e.push("Elige al menos un procedimiento.");
  if (p.piezas.some((n) => !piezaValida(n))) e.push("Revisa los números de pieza (sistema FDI).");
  if (t === "armonizacion" && !p.zonas.length) e.push("Elige al menos una zona a tratar.");
  if (t === "atm" && p.procedimientos.includes(PROC_TOXINA_ATM) && !p.zonas.length) e.push("Para la toxina botulínica elige al menos un músculo a tratar.");
  return e;
}

/** Total aplicado por unidad (U de toxina, mL de relleno). */
export function totalesEstetica(zonas: ZonaTratada[]): { u: number; ml: number } {
  let u = 0, ml = 0;
  for (const z of zonas) { if (!z.cantidad) continue; if (z.unidad === "mL") ml += z.cantidad; else u += z.cantidad; }
  return { u: Math.round(u * 10) / 10, ml: Math.round(ml * 100) / 100 };
}

export function problemasSesionEstetica(t: TipoEstetica, s: SesionEstetica): string[] {
  const e: string[] = [];
  if (t === "periodoncia") {
    if (!s.etapa) e.push("Elige la fase del tratamiento periodontal.");
    if (!s.procedimientos.length && !s.nota.trim()) e.push("Marca lo realizado o escribe una observación.");
    if (s.piezas.some((n) => !piezaValida(n))) e.push("Revisa los números de pieza (sistema FDI).");
    if (s.procedimientos.some((p) => PROC_PERIO_QUIRURGICOS.includes(p)) && !s.consentimiento) e.push("Confirma que el paciente aceptó la cirugía periodontal y firmó el consentimiento informado.");
    const fuera = (v: number | null | undefined, max: number) => v !== null && v !== undefined && (!Number.isFinite(v) || v < 0 || v > max);
    if (fuera(s.sondajeMaxMm, 15)) e.push("El sondaje máximo debe estar entre 0 y 15 mm.");
    if (fuera(s.sangradoPct, 100)) e.push("El sangrado al sondaje debe estar entre 0 y 100 %.");
    if (fuera(s.placaPct, 100)) e.push("El índice de placa debe estar entre 0 y 100 %.");
    return e;
  }
  if (t === "atm") {
    if (!s.procedimientos.length && !s.nota.trim()) e.push("Marca lo realizado o escribe una observación.");
    if (s.piezas.some((n) => !piezaValida(n))) e.push("Revisa los números de pieza (sistema FDI).");
    if (s.procedimientos.includes(PROC_PLACA_ENTREGA) && !s.arcada) e.push("Indica si la placa es de la arcada superior o inferior.");
    if (s.procedimientos.includes(PROC_DESGASTE)) {
      if (!s.piezas.length) e.push("Anota las piezas desgastadas.");
      if (!s.consentimiento) e.push("Confirma que el paciente aceptó el desgaste selectivo (es irreversible) y firmó el consentimiento.");
    }
    if (s.procedimientos.includes(PROC_TOXINA_ATM)) {
      if (!s.zonas.length) e.push("Registra al menos un músculo tratado.");
      if (s.zonas.some((z) => !z.cantidad || z.cantidad <= 0)) e.push("Indica la cantidad aplicada en cada músculo.");
      if (!s.producto.trim()) e.push("Anota el producto usado.");
      if (!s.lote.trim()) e.push("Anota el lote del producto (trazabilidad).");
      if (!s.consentimiento) e.push("Confirma que el paciente firmó el consentimiento informado.");
      if (!s.contraindicaciones) e.push("Confirma que revisaste las contraindicaciones (embarazo, lactancia, alergias, enfermedad neuromuscular, anticoagulantes, infección activa).");
    }
    return e;
  }
  if (t === "rehabilitacion") {
    if (!s.etapa) e.push("Elige la etapa del tratamiento.");
    if (!s.procedimientos.length && !s.nota.trim()) e.push("Marca lo realizado o escribe una observación.");
    if (s.piezas.some((n) => !piezaValida(n))) e.push("Revisa los números de pieza (sistema FDI).");
  } else {
    if (!s.zonas.length) e.push("Registra al menos una zona tratada.");
    if (s.zonas.some((z) => !z.cantidad || z.cantidad <= 0)) e.push("Indica la cantidad aplicada en cada zona.");
    if (!s.producto.trim()) e.push("Anota el producto usado.");
    if (!s.lote.trim()) e.push("Anota el lote del producto (trazabilidad).");
    if (!s.consentimiento) e.push("Confirma que el paciente firmó el consentimiento informado.");
    if (!s.contraindicaciones) e.push("Confirma que revisaste las contraindicaciones (embarazo, lactancia, alergias, anticoagulantes, infección activa).");
  }
  return e;
}

/** Advertencias que no impiden firmar. */
export function avisosSesionEstetica(t: TipoEstetica, s: SesionEstetica): string[] {
  if (t === "rehabilitacion") return [];
  if (t === "periodoncia") {
    const p: string[] = [];
    if ((s.sondajeMaxMm ?? 0) >= 6) p.push(`Sondaje máximo de ${s.sondajeMaxMm} mm: bolsa profunda; considera la reevaluación y, si no mejora, la fase quirúrgica.`);
    if ((s.sangradoPct ?? 0) > 30) p.push(`Sangrado al sondaje de ${s.sangradoPct} %: la inflamación sigue activa.`);
    if ((s.placaPct ?? 0) > 30) p.push(`Índice de placa de ${s.placaPct} %: refuerza la instrucción de higiene.`);
    return p;
  }
  const a: string[] = [];
  const { u, ml } = totalesEstetica(s.zonas);
  if (u > 100) a.push(`Total de toxina botulínica alto: ${u} U en una sesión. Revisa la dosis.`);
  if (ml > 4) a.push(`Volumen total de relleno alto: ${ml} mL en una sesión. Revisa el volumen.`);
  if ((s.procedimientos.includes("Toxina botulínica") || s.procedimientos.includes(PROC_TOXINA_ATM)) && s.zonas.some((z) => z.unidad === "mL" && z.cantidad && z.cantidad > 1)) a.push("La toxina se mide en unidades (U); revisa si eligió mL por error.");
  return a;
}

/**
 * Marcas del odontograma de un tratamiento estético. Rehabilitación: carilla, corona (con el material) o incrustación en cada pieza elegida
 * (una por pieza) y, si queda algo que no es de una pieza, una marca general. Armonización: una marca general con procedimientos y zonas.
 */
export function marcasEstetica(t: TipoEstetica, plan: PlanEstetica): { diente: number | null; tipo: "carilla" | "corona" | "incrustacion" | "estetica" | "armonizacion" | "atm"; nota: string }[] {
  const lista2 = (x: readonly string[]) => (x.length > 1 ? `${x.slice(0, -1).join(", ")} y ${x[x.length - 1]}` : (x[0] ?? ""));
  if (t === "periodoncia") return [];   // lo periodontal ya se marca en el odontograma con los diagnósticos automáticos y el plan de pagos
  if (t === "atm") return [{ diente: null, tipo: "atm", nota: `${lista2(plan.procedimientos)}${plan.zonas.length ? ` · ${plan.zonas.join(", ")}` : ""}${plan.piezas.length ? ` · piezas ${plan.piezas.join(", ")}` : ""}` }];
  if (t === "armonizacion") return [{ diente: null, tipo: "armonizacion", nota: `${lista2(plan.procedimientos)}${plan.zonas.length ? ` · ${plan.zonas.join(", ")}` : ""}` }];
  const p = plan.procedimientos;
  const dePieza = p.includes("Coronas estéticas") ? { tipo: "corona" as const, nota: `Corona estética${plan.material ? ` · ${plan.material}` : ""}` }
    : p.some((x) => x.startsWith("Carillas")) ? { tipo: "carilla" as const, nota: p.includes("Carillas de resina") && !p.includes("Carillas de porcelana") ? "Carilla de resina" : "Carilla de porcelana" }
    : p.includes("Incrustaciones estéticas") ? { tipo: "incrustacion" as const, nota: "Incrustación estética" } : null;
  const marcas: { diente: number | null; tipo: "carilla" | "corona" | "incrustacion" | "estetica" | "armonizacion" | "atm"; nota: string }[] = [];
  if (dePieza) for (const n of plan.piezas) marcas.push({ diente: n, ...dePieza });
  const resto = p.filter((x) => !(dePieza && plan.piezas.length && (x === "Coronas estéticas" || x.startsWith("Carillas") || x === "Incrustaciones estéticas")));
  if (resto.length) marcas.push({ diente: null, tipo: "estetica", nota: lista2(resto) });
  return marcas;
}

const lista = (x: string[]) => (x.length > 1 ? `${x.slice(0, -1).join(", ")} y ${x[x.length - 1]}` : (x[0] ?? ""));

export function resumenSesionEstetica(t: TipoEstetica, plan: PlanEstetica, s: SesionEstetica): string {
  const proc = s.procedimientos.length ? s.procedimientos : plan.procedimientos;
  if (t === "rehabilitacion") {
    const partes = [`Rehabilitación y estética dental · ${s.etapa.toLowerCase()}.`];
    if (proc.length) partes.push(`${lista(proc)}${s.piezas.length ? ` en piezas ${s.piezas.join(", ")}` : ""}.`);
    if (s.color) partes.push(`Color: ${s.color}.`);
    if (plan.material) partes.push(`Material: ${plan.material}.`);
    if (s.nota.trim()) partes.push(s.nota.trim());
    return partes.join(" ");
  }
  if (t === "periodoncia") {
    const pp = [`Periodoncia · ${s.etapa.toLowerCase()}.`];
    if (proc.length) pp.push(`${lista(proc)}${s.cuadrantes?.length ? ` (${s.cuadrantes.join(", ")})` : ""}${s.piezas.length ? ` en piezas ${s.piezas.join(", ")}` : ""}.`);
    const med = [s.sondajeMaxMm != null ? `sondaje máximo ${s.sondajeMaxMm} mm` : "", s.sangradoPct != null ? `sangrado al sondaje ${s.sangradoPct} %` : "", s.placaPct != null ? `índice de placa ${s.placaPct} %` : ""].filter(Boolean);
    if (med.length) pp.push(`Medidas: ${med.join("; ")}.`);
    if (s.anestesia && s.anestesia !== "Ninguna") pp.push(`Anestesia ${s.anestesia.toLowerCase()}.`);
    if (s.procedimientos.some((x) => PROC_PERIO_QUIRURGICOS.includes(x))) pp.push("Consentimiento informado firmado.");
    if (s.nota.trim()) pp.push(s.nota.trim());
    if (s.proximoSemanas) pp.push(`Control en ${s.proximoSemanas} semana(s).`);
    return pp.join(" ");
  }
  if (t === "atm") {
    const pa = [`ATM y bruxismo: ${lista(proc).toLowerCase() || "control"}.`];
    if (s.arcada) pa.push(`Placa de la arcada ${s.arcada.toLowerCase()}.`);
    if (plan.material) pa.push(`Material: ${plan.material}.`);
    if (s.piezas.length && s.procedimientos.includes(PROC_DESGASTE)) pa.push(`Desgaste selectivo en piezas ${s.piezas.join(", ")}.`);
    if (s.procedimientos.includes(PROC_TOXINA_ATM)) {
      const tz = s.zonas.map((z) => z.zona + " " + z.cantidad + " " + z.unidad).join("; ");
      pa.push("Toxina botulínica: " + tz + ".", "Producto: " + s.producto + (s.marca ? " (" + s.marca + ")" : "") + ", lote " + s.lote + ".");
      if (s.anestesia && s.anestesia !== "Ninguna") pa.push("Anestesia " + s.anestesia.toLowerCase() + ".");
      pa.push("Consentimiento informado firmado y contraindicaciones revisadas.");
    } else if (s.procedimientos.includes(PROC_DESGASTE)) pa.push("El paciente aceptó el desgaste selectivo (consentimiento firmado).");
    if (s.nota.trim()) pa.push(s.nota.trim());
    if (s.proximoSemanas) pa.push(`Control en ${s.proximoSemanas} semana(s).`);
    return pa.join(" ");
  }
  const zonas = s.zonas.map((z) => `${z.zona} ${z.cantidad} ${z.unidad}`);
  const partes = [`Armonización orofacial: ${lista(proc).toLowerCase() || "procedimiento"}.`, `Zonas: ${zonas.join("; ")}.`, `Producto: ${s.producto}${s.marca ? ` (${s.marca})` : ""}, lote ${s.lote}.`];
  if (s.tecnica) partes.push(`Técnica: ${s.tecnica.toLowerCase()}.`);
  if (s.anestesia && s.anestesia !== "Ninguna") partes.push(`Anestesia ${s.anestesia.toLowerCase()}.`);
  partes.push("Consentimiento informado firmado y contraindicaciones revisadas.");
  if (s.nota.trim()) partes.push(s.nota.trim());
  if (s.proximoSemanas) partes.push(`Control en ${s.proximoSemanas} semana(s).`);
  return partes.join(" ");
}
